import React, { useEffect, useMemo, useState } from "react";
import { Baby, Plus, Pencil, Trash2, Check, X, User, CalendarClock, ChevronDown, ChevronRight } from "lucide-react";
import { apiFetch, useApiData } from "../lib/api.js";
import { getCurrentUser } from "../lib/auth.js";
import InfoTip from "./InfoTip.jsx";
import ConfirmDialog from "./ConfirmDialog.jsx";
import { useUzkaObrazovka } from "../lib/obrazovka.js";

// Panel "Děti / permice" v detailu rodičovského klienta.
// Data z /api/client-deti (samostatná tabulka client_deti, NENÍ v clients), takže se
// permice dětí nepřičítá rodiči. Vzniká hlavně automaticky z FAPI faktury, tady jde o
// zobrazení + editaci (jména dětí, trenér, počet permic) a ruční přidání.

const EMPTY_FORM = { nazev: "", jmena_deti: "", pocet_deti: 1, trener_id: "", permice_celkem: 10, permice_zbyva: 10, platnost_do: "2026-08-31", poznamka: "", poznamka_trener: "" };

// /api/clients vraci jen `notion` URL (32hex bez pomlcek), ne notion_id. Vytahneme id.
function notionIdFromUrl(u) {
  const m = String(u || "").match(/([0-9a-f]{32}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
  return m ? m[1] : "";
}

export default function ClientDetiSection({ klientId, klientNotion, klientName, readOnly = false, bezRamce = false, onCount }) {
  const parentId = klientId || notionIdFromUrl(klientNotion);
  // 9.9.2026 — telefonní verze karty: název ve fialovém pruhu, čísla jako dlaždice.
  const uzka = useUzkaObrazovka();
  const [deti, setDeti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null); // id | "new" | null
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // dite k smazani (designový confirm)
  // 29.9.2026 — vyčerpané a propadlé permice se schovávají pod řádek "Minulé permice",
  // jinak se sekce u rodičů s více nákupy nafukuje a aktivní permice v ní zanikne.
  const [minuleOtevrene, setMinuleOtevrene] = useState(false);

  const user = getCurrentUser();
  const role = user?.role || "";
  // readOnly (manažerský náhled) = jen čtení, žádné edit/přidat/smazat.
  const canEdit = !readOnly && ["owner", "manager", "head_coach", "trener"].includes(role);
  const canManage = !readOnly && ["owner", "manager"].includes(role);
  const canManageMeta = !readOnly && ["owner", "manager", "head_coach"].includes(role); // smí měnit název (Reservio) + trenéra

  const { data: hrData } = useApiData("/api/hr-list-people?filter=treneri&status=aktivni");
  const trenerOptions = useMemo(() => {
    const people = (hrData && hrData.people) || [];
    return people
      .filter((p) => p.is_trener && p.active !== false)
      .map((p) => ({ id: p.id, name: p.display_name }))
      .sort((a, b) => a.name.localeCompare(b.name, "cs"));
  }, [hrData]);

  const load = async () => {
    if (!parentId) { setLoading(false); return; }
    try {
      const r = await apiFetch(`/api/client-deti?parentId=${encodeURIComponent(parentId)}`);
      const j = await r.json();
      setDeti(Array.isArray(j.deti) ? j.deti : []);
    } catch (_) { /* ticho */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, [parentId]);

  // Odznak v hlavičce sekce: u jedné permice zbývá/celkem, jinak počet. null znamená
  // "sekci vůbec nezobrazuj" (klient nemá permici a uživatel ji ani nesmí přidat).
  useEffect(() => {
    if (typeof onCount !== "function" || loading) return;
    if (deti.length === 0) { onCount(canManage ? 0 : null); return; }
    if (deti.length === 1) { onCount(`${deti[0].permice_zbyva} z ${deti[0].permice_celkem}`); return; }
    onCount(`${deti.length}×`);
  }, [deti, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const startNew = () => { setForm({ ...EMPTY_FORM }); setEditId("new"); };
  const startEdit = (d) => {
    setForm({
      nazev: d.nazev || "", jmena_deti: d.jmena_deti || "", pocet_deti: d.pocet_deti || 1,
      trener_id: d.trener_id || "", permice_celkem: d.permice_celkem || 10, permice_zbyva: d.permice_zbyva || 0,
      platnost_do: d.platnost_do || "2026-08-31", poznamka: d.poznamka || "", poznamka_trener: d.poznamka_trener || "",
    });
    setEditId(d.id);
  };
  const cancel = () => { setEditId(null); setForm(EMPTY_FORM); };

  const save = async () => {
    if (!form.nazev.trim()) return;
    setSaving(true);
    try {
      if (editId === "new") {
        await apiFetch("/api/client-deti", { method: "POST", body: JSON.stringify({ action: "create", parent_notion_id: parentId, ...form }) });
      } else {
        // Auto pole (pocet, permice, platnost, zdrojova poznamka) se NEPOSÍLAJÍ — počítají se samy.
        // Backend navíc nazev/trener pustí jen owner/manager/head_coach; jmena+poznamka_trener i trenérovi.
        const fields = { nazev: form.nazev, jmena_deti: form.jmena_deti, trener_id: form.trener_id, poznamka_trener: form.poznamka_trener, platnost_do: form.platnost_do };
        await apiFetch("/api/client-deti", { method: "POST", body: JSON.stringify({ action: "update", id: editId, fields }) });
      }
      await load();
      cancel();
    } catch (_) { /* ticho */ }
    setSaving(false);
  };

  const remove = (id) => setConfirmDelete(id);
  const removeConfirmed = async (id) => {
    try {
      await apiFetch("/api/client-deti", { method: "POST", body: JSON.stringify({ action: "delete", id }) });
      await load();
    } catch (_) { /* ticho */ }
  };

  // Minulá permice = vyčerpaná (zbývá 0) nebo jí skončila platnost.
  const dnesIso = new Date().toISOString().slice(0, 10);
  const jeMinula = (d) => Number(d.permice_zbyva) <= 0 || (!!d.platnost_do && d.platnost_do < dnesIso);
  const stitekMinula = (d) => (!!d.platnost_do && d.platnost_do < dnesIso ? "Propadlá" : "Vyčerpaná");
  const aktivni = deti.filter((d) => !jeMinula(d));
  const minule = deti.filter((d) => jeMinula(d));

  if (loading) return null;
  if (deti.length === 0 && !canManage) return null; // bez dětí nezobrazuj běžnému trenérovi

  const karta = (d, minula) => (
          editId === d.id ? (
            <DetiForm key={d.id} form={form} setForm={setForm} trenerOptions={trenerOptions} canManageMeta={canManageMeta} isNew={false} onSave={save} onCancel={cancel} saving={saving} inputCls="field" />
          ) : (
            <div key={d.id} className={`rounded-xl border border-slate-200 bg-white p-4 ${minula ? "opacity-75" : ""}`}>
              {/* Hlavička: avatar + název + štítky + akce.
                  9.9.2026 telefon: název ve fialovém pruhu, akce jako dvě velká tlačítka. */}
              {uzka && (
                <>
                  <div className="rounded-xl bg-brand-100 px-3 py-2">
                    <span className="text-[15px] font-extrabold leading-tight tracking-tight text-brand-800">{d.nazev}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className="inline-flex items-center gap-1 rounded-full border border-plum-100 bg-plum-50 px-2 py-0.5 text-xs font-semibold text-plum-700">{d.pocet_deti === 2 ? "2 děti" : "1 dítě"}</span>
                    <span className="inline-flex items-center gap-1 rounded-full border border-brand-100 bg-brand-50 px-2 py-0.5 text-xs font-semibold text-brand-700">Permice</span>
                    {minula && <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">{stitekMinula(d)}</span>}
                  </div>
                  {(canEdit || canManage) && (
                    <div className="mt-2 flex items-center gap-2">
                      {canEdit && (
                        <button onClick={() => startEdit(d)} aria-label="Upravit" title="Upravit"
                          className="flex h-11 flex-1 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600">
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}
                      {canManage && (
                        <button onClick={() => remove(d.id)} aria-label="Smazat" title="Smazat"
                          className="flex h-11 flex-1 items-center justify-center rounded-xl border border-rose-200 bg-white text-rose-600">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
              <div className={`${uzka ? "hidden" : "flex"} items-center gap-3`}>
                <div className="w-10 h-10 rounded-full bg-plum-100 text-plum-600 flex items-center justify-center flex-shrink-0">
                  <Baby className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-base font-semibold text-slate-800 truncate">{d.nazev}</div>
                  <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-plum-700 bg-plum-50 border border-plum-100 rounded-full px-2 py-0.5">{d.pocet_deti === 2 ? "2 děti" : "1 dítě"}</span>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 bg-brand-50 border border-brand-100 rounded-full px-2 py-0.5">Permice</span>
                    {minula && <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">{stitekMinula(d)}</span>}
                  </div>
                </div>
                {canEdit && (
                  <button onClick={() => startEdit(d)} aria-label="Upravit" className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-plum-700 hover:bg-plum-50 rounded-xl">
                    <Pencil className="w-4 h-4" />
                  </button>
                )}
                {canManage && (
                  <button onClick={() => remove(d.id)} aria-label="Smazat" className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Jména dětí jako pill boxy */}
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                {String(d.jmena_deti || "").split(",").map((s) => s.trim()).filter(Boolean).length > 0
                  ? String(d.jmena_deti).split(",").map((s) => s.trim()).filter(Boolean).map((nm, i) => (
                      <span key={i} className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 bg-slate-100 rounded-full pl-1 pr-3 py-1">
                        <span className="w-6 h-6 rounded-full bg-white text-plum-600 flex items-center justify-center"><User className="w-3.5 h-3.5" /></span>
                        {nm}
                      </span>
                    ))
                  : <span className="text-sm text-slate-400 italic">Jména dětí doplní trenér</span>}
              </div>

              {/* 9.9.2026 telefon: tři dlaždice s čísly místo pruhu a řádku "Platí do".
                  Zbývající permice je zeleně, ať je hned vidět, kolik zbývá. */}
              {uzka && (
                <>
                  <div className="mt-2.5 grid grid-cols-3 gap-1.5">
                    <div className="rounded-xl bg-slate-50 py-2 text-center">
                      <div className="text-[19px] font-extrabold leading-none text-slate-800 tabular-nums">{d.permice_celkem}</div>
                      <div className="mt-1 text-[11px] font-semibold text-slate-500">celkem</div>
                    </div>
                    <div className="rounded-xl bg-emerald-50 py-2 text-center">
                      <div className="text-[19px] font-extrabold leading-none text-emerald-700 tabular-nums">{d.permice_zbyva}</div>
                      <div className="mt-1 text-[11px] font-semibold text-emerald-700">zbývá</div>
                    </div>
                    <div className="rounded-xl bg-slate-50 py-2 text-center">
                      <div className="pt-0.5 text-[15px] font-extrabold leading-none text-slate-800 tabular-nums">
                        {d.platnost_do ? `${d.platnost_do.split("-").reverse().slice(0, 2).join(". ")}.` : "—"}
                      </div>
                      <div className="mt-1 text-[11px] font-semibold text-slate-500">platí do</div>
                    </div>
                  </div>
                  <div className="mt-2">
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-sm font-medium ${d.trener_name ? "bg-plum-50 text-plum-700" : "bg-slate-100 text-slate-400"}`}>
                      <User className="h-3.5 w-3.5" /> {d.trener_name || "nepřiřazen"}
                    </span>
                  </div>
                </>
              )}
              {/* Permice (sdílená) + trenér */}
              <div className={`${uzka ? "hidden" : "grid"} grid-cols-1 sm:grid-cols-2 gap-4 mt-4`}>
                <div>
                  <div className="text-sm text-slate-500 mb-1.5">Zbývá tréninků (sdílená permice)</div>
                  <div className="flex items-center gap-2.5">
                    <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-plum-500 rounded-full" style={{ width: `${d.permice_celkem ? Math.round((d.permice_zbyva / d.permice_celkem) * 100) : 0}%` }} />
                    </div>
                    <span className="text-base font-bold text-slate-800 tabular-nums">{d.permice_zbyva}<span className="text-sm font-medium text-slate-400"> / {d.permice_celkem}</span></span>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-slate-500 mb-1.5">Trenér</div>
                  <span className={`inline-flex items-center gap-1.5 text-sm font-medium rounded-full px-2.5 py-1 ${d.trener_name ? "text-plum-700 bg-plum-50" : "text-slate-400 bg-slate-100"}`}>
                    <User className="w-3.5 h-3.5" /> {d.trener_name || "nepřiřazen"}
                  </span>
                </div>
              </div>

              {/* Platnost + poznámka k tréninku (na telefonu je platnost v dlaždici výše) */}
              <div className={`${uzka ? "hidden" : "flex"} items-center gap-2 mt-4 pt-3 border-t border-slate-100 text-sm text-slate-500 flex-wrap`}>
                <CalendarClock className="w-4 h-4 text-slate-400" /> Platí do <span className="font-medium text-slate-700">{d.platnost_do ? d.platnost_do.split("-").reverse().join(".") : "—"}</span>
              </div>
              {d.poznamka_trener && (
                <div className="mt-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-xl p-2.5 whitespace-pre-wrap">{d.poznamka_trener}</div>
              )}

              {/* Termíny dětí (historie + budoucí) — rozbalitelné, ať se nepletou s rodičem */}
              <DetiTrainings bookings={d.bookings} />
            </div>
          )
  );

  return (
    <div className={bezRamce ? "" : "bg-white rounded-xl border border-slate-200 p-4 sm:p-5 shadow-sm"}>
      {/* 9.9.2026 — v detailu klienta hlavičku kreslí ExpandableSection, tady zbyde
          jen nápověda a tlačítko Přidat. */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {!bezRamce && <Baby className="w-5 h-5 text-plum-600" />}
        {!bezRamce && <h3 className="text-base font-semibold text-slate-900">Děti / permice</h3>}
        <InfoTip text="Dětská permice navázaná na tohoto klienta (akce léto 2026). Vede ji libovolný trenér. Nepočítá se do MRR ani tréninků rodiče, fakturu rodiče s touto položkou automatika ignoruje. Jedna permice může pokrýt 1 nebo 2 děti, jeden trénink = jeden odečet (nikdy spojený)." />
        {canManage && editId !== "new" && (
          <button onClick={startNew} className="ml-auto inline-flex items-center gap-1.5 text-sm font-semibold text-plum-700 border border-plum-200 rounded-xl px-2.5 py-1.5 hover:bg-plum-50 min-h-[44px] sm:min-h-0">
            <Plus className="w-4 h-4" /> Přidat
          </button>
        )}
      </div>

      {deti.length === 0 && editId !== "new" && (
        <div className="text-sm text-slate-400">Žádná dětská permice.</div>
      )}

      <div className="space-y-3">
        {aktivni.map((d) => karta(d, false))}

        {minule.length > 0 && (
          <>
            <button
              type="button"
              onClick={() => setMinuleOtevrene((v) => !v)}
              className="flex w-full min-h-[44px] items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-left transition hover:bg-slate-100"
            >
              {minuleOtevrene ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
              <span className="text-sm font-semibold text-slate-600">Minulé permice</span>
              <span className="ml-auto inline-flex items-center rounded-full bg-slate-200 px-2 py-0.5 text-xs font-bold text-slate-600">{minule.length}</span>
            </button>
            {minuleOtevrene && minule.map((d) => karta(d, true))}
          </>
        )}

        {editId === "new" && (
          <DetiForm form={form} setForm={setForm} trenerOptions={trenerOptions} canManageMeta={canManageMeta} isNew={true} onSave={save} onCancel={cancel} saving={saving} inputCls="field" />
        )}
      </div>
      <ConfirmDialog
        open={!!confirmDelete}
        title="Smazat dětskou permici?"
        description="Permice se smaže i s údaji o dětech. Tohle nejde vrátit."
        confirmLabel="Smazat"
        variant="danger"
        onConfirm={() => { const id = confirmDelete; setConfirmDelete(null); removeConfirmed(id); }}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}

// Rozbalitelný přehled dětských tréninků (budoucí + odchozené letos). Oddělené od
// tréninků rodiče, aby se v admin detailu nepletly. Data z /api/client-deti (d.bookings).
function DetiTrainings({ bookings }) {
  const uzka = useUzkaObrazovka();
  const [open, setOpen] = useState(false);
  const all = Array.isArray(bookings) ? bookings : [];
  const todayKey = new Date().toISOString().slice(0, 10);
  const DOW = ["neděle", "pondělí", "úterý", "středa", "čtvrtek", "pátek", "sobota"];
  const MG = ["ledna", "února", "března", "dubna", "května", "června", "července", "srpna", "září", "října", "listopadu", "prosince"];
  const fmtDay = (ds) => { const dt = new Date(ds); const nm = DOW[dt.getDay()]; return nm.charAt(0).toUpperCase() + nm.slice(1) + " " + dt.getDate() + ". " + MG[dt.getMonth()]; };
  const future = all.filter((b) => b.date >= todayKey).sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
  const past = all.filter((b) => b.date < todayKey).sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time));
  const row = (b, k, isFuture) => (
    <div key={k} className={`flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl border px-3 py-2 ${isFuture ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
      <span className="text-base">🧒</span>
      <span className={`text-sm font-semibold min-w-[120px] ${isFuture ? "text-slate-800" : "text-slate-500"}`}>{fmtDay(b.date)}</span>
      <span className={`text-sm font-semibold ${isFuture ? "text-emerald-700" : "text-slate-500"}`}>{b.time}</span>
      {b.trainer && <span className="ml-auto text-xs text-slate-500 inline-flex items-center gap-1"><User className="w-3 h-3" />{b.trainer}</span>}
    </div>
  );
  return (
    <div className="mt-3 pt-3 border-t border-slate-100">
      {/* 9.9.2026 telefon: krátký řádek s odznakem místo věty, která se lámala na tři řádky. */}
      {uzka ? (
        <button onClick={() => setOpen(!open)}
          className="flex min-h-[44px] w-full items-center justify-between gap-2 rounded-xl border border-plum-200 bg-plum-50 px-3 text-[13.5px] font-bold text-plum-700">
          <span>Termíny dětí</span>
          <span className="flex items-center gap-1.5">
            <span className="rounded-full bg-white px-2 py-0.5 text-[11.5px] font-bold text-plum-700">{future.length} budoucí</span>
            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </span>
        </button>
      ) : (
        <button onClick={() => setOpen(!open)} className="w-full inline-flex items-center justify-center gap-2 text-sm font-semibold text-plum-700 bg-plum-50 border border-plum-100 rounded-xl px-3 py-2 hover:bg-plum-100">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          {open ? "Skrýt" : "Zobrazit"} termíny dětí ({future.length} budoucích, {past.length} odchozených)
        </button>
      )}
      {open && (
        <div className="mt-3 space-y-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-emerald-700 mb-2">Budoucí (max 4 týdny)</div>
            <div className="space-y-1.5">
              {future.length > 0 ? future.map((b, i) => row(b, "f" + i, true)) : <div className="text-sm text-slate-400">Žádný naplánovaný trénink.</div>}
            </div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Odchozené (letos)</div>
            <div className="space-y-1.5">
              {past.length > 0 ? past.map((b, i) => row(b, "p" + i, false)) : <div className="text-sm text-slate-400">Zatím žádný odchozený trénink.</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetiForm({ form, setForm, trenerOptions, canManageMeta, isNew, onSave, onCancel, saving, inputCls }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // Per-dítě jména: jmena_deti se ukládá jako "Adélka, Tomík", v UI 1 nebo 2 pole dle počtu dětí.
  const pocet = form.pocet_deti === 2 ? 2 : 1;
  const namesArr = (() => { const a = String(form.jmena_deti || "").split(",").map((s) => s.trim()); const out = []; for (let i = 0; i < pocet; i++) out.push(a[i] || ""); return out; })();
  const setName = (i, v) => { const a = [...namesArr]; a[i] = v; set("jmena_deti", a.join(", ").replace(/(,\s*)+$/, "")); };
  const trenerName = (trenerOptions.find((t) => t.id === form.trener_id) || {}).name || "nepřiřazen";
  const platiDo = form.platnost_do ? form.platnost_do.split("-").reverse().join(".") : "—";
  const roBox = `${inputCls} bg-slate-100 text-slate-600`;
  return (
    <div className="bg-plum-50/50 border border-plum-200 rounded-xl p-3 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Název (Reservio) — jen owner/manager */}
        <label className="block">
          <span className="text-sm text-slate-600">Název (jako v Reserviu)</span>
          {canManageMeta
            ? <input className={inputCls} value={form.nazev} onChange={(e) => set("nazev", e.target.value)} placeholder="Dítě J. Smržová" />
            : <div className={roBox}>{form.nazev || "—"}</div>}
          {canManageMeta && (
            <span className="mt-1 block text-xs text-slate-500">
              Musí sedět se jménem, pod kterým se dítě objednává v Reserviu. Podle něj se k permici
              počítají tréninky, takže u sourozenců patří každé permici jméno té dcery nebo syna,
              komu byla koupená.
            </span>
          )}
        </label>
        {/* Trenér — jen owner/manager */}
        <label className="block">
          <span className="text-sm text-slate-600">Trenér</span>
          {canManageMeta
            ? <select className={inputCls} value={form.trener_id} onChange={(e) => set("trener_id", e.target.value)}>
                <option value="">nepřiřazen</option>
                {trenerOptions.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            : <div className={roBox}>{trenerName}</div>}
        </label>
        {/* Jména dětí — JEDINÉ co může upravit i trenér */}
        {namesArr.map((nm, i) => (
          <label className="block" key={i}>
            <span className="text-sm text-slate-600">{pocet === 2 ? `Jméno dítěte ${i + 1}` : "Jméno dítěte"}</span>
            <input className={inputCls} value={nm} onChange={(e) => setName(i, e.target.value)} placeholder="Jméno" />
          </label>
        ))}
        {/* Počet dětí — AUTO (u manuálního přidání volí manager) */}
        <label className="block">
          <span className="text-sm text-slate-600">Počet dětí</span>
          {isNew && canManageMeta
            ? <select className={inputCls} value={form.pocet_deti} onChange={(e) => set("pocet_deti", parseInt(e.target.value, 10))}>
                <option value={1}>1 dítě</option>
                <option value={2}>2 děti</option>
              </select>
            : <div className={roBox}>{pocet === 2 ? "2 děti" : "1 dítě"}</div>}
        </label>
      </div>

      {/* AUTO přehled — permice se počítá z faktury/Reservia. Platnost smí ručně upravit owner/manager. */}
      <div className="grid grid-cols-1 gap-3 rounded-xl border border-slate-200 bg-white/70 p-3 sm:grid-cols-3">
        <div><div className="text-sm text-slate-500">Permice celkem</div><div className="text-base font-semibold text-slate-700">{form.permice_celkem}</div></div>
        <div><div className="text-sm text-slate-500">Permice zbývá</div><div className="text-base font-semibold text-slate-700">{form.permice_zbyva}</div></div>
        <div>
          <div className="text-sm text-slate-500">Platí do</div>
          {canManageMeta
            ? <input type="date" className={inputCls} value={form.platnost_do || ""} onChange={(e) => set("platnost_do", e.target.value)} />
            : <div className="text-base font-semibold text-slate-700">{platiDo}</div>}
        </div>
      </div>
      <div className="text-sm text-slate-500">Počet dětí a počet tréninků se počítají automaticky (z faktury a Reservia). Platnost permice lze ručně upravit (owner/manager).</div>

      {/* Poznámky k tréninku — větší pole pro trenéra */}
      <label className="block">
        <span className="text-sm text-slate-600">Poznámky k tréninku</span>
        <textarea className={`${inputCls} min-h-[100px] resize-y`} rows={4} value={form.poznamka_trener} onChange={(e) => set("poznamka_trener", e.target.value)} placeholder="Termín, pokyny, poznámky k dětem…" />
      </label>
      {form.poznamka && <div className="text-sm text-slate-400">Zdroj: {form.poznamka}</div>}

      <div className="flex items-center gap-2">
        <button onClick={onSave} disabled={saving || !form.nazev.trim()} className="btn btn-primary">
          <Check className="w-4 h-4" /> Uložit
        </button>
        <button onClick={onCancel} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 border border-slate-300 rounded-xl px-3 py-1.5 hover:bg-slate-50 min-h-[44px] sm:min-h-0">
          <X className="w-4 h-4" /> Zrušit
        </button>
      </div>
    </div>
  );
}
